import moment from 'moment';
import { useQuery } from '@tanstack/react-query';

import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import forEach from 'lodash/forEach';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { API } from 'src/reactQuery/Api';
import { forceString } from 'src/utils/datetime';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { buildChild, buildAttributes } from 'src/utils/tools';

import type { Moment } from 'moment';

export interface CandidateJobShiftFiltersType {
  job_id: string;
  user_id: string;
  to_date: Moment | null;
  from_date: Moment | null;
  project_id: string;
  status: UCM.CandidateJobShiftStatusType;
}

type IncludedDataResponse<
  TDataAttributes = unknown,
  TIncluded = unknown,
  TMeta = unknown,
> = {
  data: Array<{
    id: string;
    type: string;
    attributes: TDataAttributes;
    relationships: {
      user: { data: { id: string; type: string } | null };
      user_job_payroll: { data: { id: string; type: string } };
    };
  }>;
  included: Array<{
    id: string;
    type: string;
    attributes: TIncluded;
  }>;
  meta: TMeta;
};

export const mapCandidateJobShift = (
  records: IncludedDataResponse<UCM.CandidateJobShiftRawType>['data'],
  included: Record<
    string,
    { id: string; type: string; attributes: UCM.CandidateJobShiftIncludedType }
  >,
): Array<UCM.CandidateJobShiftType> => {
  return map(records, (record) => {
    const payroll = buildChild(record, included, 'user_job_payroll', null, {
      roundedCheckOut: 'checkout_arrounded',
      roundedCheckIn: 'checkin_arrounded',
      billableHours: 'billable_hour',
      breakHours: 'break_in_hour',
      nightHours: 'night_hour',
      sundayHours: 'sunday_hour',
      holidayHours: 'holiday_hour',
      priceBonus: 'price_bonus',
      wageBonus: 'wage_bonus',
      salary: 'wage_sum',
      price: 'price',
      wage: 'wage',
    });

    const timeTracking = buildAttributes(record, {
      minWorkingHours: 'min_working_hours',
      isBreakPaid: 'is_break_paid',
      projectUuid: 'project_uuid',
      projectId: 'project_id',
      jobId: 'job_id',
      jobUuid: 'job_uuid',
      uuid: 'uuid',
      isBreakTaken: 'break',
      checkIn: 'checkin_time',
      checkOut: 'checkout_time',
      checkinTimeSource: 'checkin_time_source',
      checkoutTimeSource: 'checkout_time_source',
      checkinDataSource: 'checkin_data_source',
      checkoutDataSource: 'checkout_data_source',
      startTime: 'shift_starts_at',
      endTime: 'shift_ends_at',
      status: 'status_code',
      adminComment: 'admin_comment',
      clientComment: 'client_comment',
      clientCheckin: 'client_checkin_time',
      clientCheckout: 'client_checkout_time',
      clientUserRating: 'client_user_rating',
      isContractSigned: 'is_contract_signed',
      isBriefingConfirmed: 'is_briefing_confirmed',
      isCheckinAutoApproved: 'is_checkin_auto_approved',
      isCheckoutAutoApproved: 'is_checkout_auto_approved',
      isCheckoutAsPlanned: 'is_checkout_as_planned',
      isCheckinAsPlanned: 'is_checkin_as_planned',
      checkinStatus: 'checkin_status',
      checkoutStatus: 'checkout_status',
      checkinDeviation: 'checkin_deviation',
      checkoutDeviation: 'checkout_deviation',
      checkinNoCodeReason: 'checkin_no_code_reason',
      checkoutNoCodeReason: 'checkout_no_code_reason',
      checkinLocationLatitude: 'checkin_location_latitude',
      checkinLocationLongitude: 'checkin_location_longitude',
      checkoutLocationLatitude: 'checkout_location_latitude',
      checkoutLocationLongitude: 'checkout_location_longitude',
      checkinDeviationReason: () =>
        record.attributes.checkin_deviation_reason.code,
      checkoutDeviationReason: () =>
        record.attributes.checkout_deviation_reason.code,
      cancellation: () => ({
        id: record.attributes.cancellation_notice?.id,
        uuid: record.attributes.cancellation_notice?.uuid,
      }),
      user: () => buildChild(record, included, 'user'),
    }) as unknown as UCM.CandidateJobShiftType;

    const fieldsToParse = [
      'priceBonus',
      'wageBonus',
      'salary',
      'price',
      'wage',
    ];

    forEach(payroll, (value, field) => {
      if (field !== 'id' && field !== 'uuid') {
        // eslint-disable-next-line
        (timeTracking as any)[field] =
          value && fieldsToParse.includes(field) ? parseFloat(value) : value;
      }
    });
    return timeTracking;
  });
};

const getCandidateJobShifts = async (
  filters: CandidateJobShiftFiltersType,
  params: Record<string, number>,
) => {
  const { status, ...otherFilters } = filters;

  const updatedStatusFilter =
    status === 'submitted' ? ['submitted', 'verified'] : status;

  const updatedFilters = {
    ...otherFilters,
    status: updatedStatusFilter,
    from_date: forceString(filters.from_date, moment.HTML5_FMT.DATE),
    to_date: forceString(filters.to_date, moment.HTML5_FMT.DATE),
  };

  const query = resolveQueryString([updatedFilters], params);

  const {
    data: { data, meta, included },
  } = await getRequest<
    IncludedDataResponse<
      UCM.CandidateJobShiftRawType,
      UCM.CandidateJobShiftIncludedType,
      UCM.MetaRawType
    >
  >(`${API.GET.getCandidateJobShifts}${query}`);

  const includedRecords = keyBy(included, (item) => `${item.id}-${item.type}`);

  return {
    meta: paginationMapper(meta),
    data: mapCandidateJobShift(data, includedRecords),
  };
};

export const useGetCandidateJobShifts = (
  params: Record<string, number>,
  filters: CandidateJobShiftFiltersType,
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [
      QUERY_KEYS.CANDIDATE_JOB_SHIFTS,
      {
        ...filters,
        from_date: forceString(filters.from_date, moment.HTML5_FMT.DATE),
        to_date: forceString(filters.to_date, moment.HTML5_FMT.DATE),
      },
      params,
    ],
    queryFn: () => getCandidateJobShifts(filters, params),
    staleTime: 1000 * 60 * 5,
    ...config,
  });
};
